/**
 * ч.1436 (рішення власника): обробка 1С у консолі платформи. Користувачі на сторінці
 * перенесення отримують лише готову обробку; вихідні тексти — тут, і лише власнику.
 * Готовий файл бекенд бере з теки import-sources на сервері (найсвіжіший «ПереносДаних*.epf»).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { platformMigration1c, type Migration1cFile } from '@/api/platformMigration1c';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const size = (b: number) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} МБ` : `${Math.max(1, Math.round(b / 1024))} КБ`);
const when = (iso: string) => new Date(iso).toLocaleString('uk-UA', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

export default function Migration1cTab() {
  const q = useQuery({ queryKey: ['platform-migration-1c'], queryFn: platformMigration1c.overview });
  const [error, setError] = useState<string | null>(null);
  const run = (fn: () => Promise<void>) => {
    setError(null);
    fn().catch(() => setError('Не вдалося завантажити файл.'));
  };
  const columns = (onClick: (f: Migration1cFile) => void): ColumnDef<Migration1cFile>[] => [
    { key: 'file_name', header: 'Файл', sortValue: (f) => f.file_name,
      cell: (f) => <button type="button" className="btn-link" onClick={() => onClick(f)}>{f.file_name}</button> },
    { key: 'size', header: 'Розмір', width: 110, align: 'right', sortValue: (f) => f.size, cell: (f) => size(f.size) },
    { key: 'updated_at', header: 'Змінено', width: 170, sortValue: (f) => f.updated_at, cell: (f) => when(f.updated_at) },
  ];
  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Обробка 1С</h2>
      {error && <div className="mc__panel mc__panel--warn" role="alert">{error}</div>}
      <h3>Готова обробка</h3>
      <p style={{ color: 'var(--muted)', marginTop: 0 }}>
        Її отримують користувачі на сторінці перенесення. Нова версія — файл «ПереносДаних…epf» у теці
        import-sources на сервері: віддається найсвіжіший, без викатки.
      </p>
      {q.data && (q.data.processing ? (
        <DataTable rows={[q.data.processing]} rowKey={(f) => f.file_name}
          columns={columns((f) => run(() => platformMigration1c.downloadProcessing(f.file_name)))} />
      ) : <p>Файл ще не покладено в теку.</p>)}
      <h3>Вихідні тексти</h3>
      <p style={{ color: 'var(--muted)', marginTop: 0 }}>
        Модулі форми і обʼєкта, інструкції зі збирання і службові скрипти. Користувачам вони недоступні.
      </p>
      <DataTable rows={q.data?.files ?? []} rowKey={(f) => f.file_name} loading={q.isLoading}
        emptyText="Файлів немає" initialSortKey="file_name"
        columns={columns((f) => run(() => platformMigration1c.downloadSource(f.file_name)))} />
    </div>
  );
}
